import { useCallback, useMemo } from 'react';

/** Global reference to AudioContext to share it across components */
let globalAudioCtx = null;

export const useSoundEngine = () => {
  /** Ottiene (o crea) l'AudioContext */
  const getCtx = useCallback(() => {
    if (!globalAudioCtx || globalAudioCtx.state === 'closed') {
      globalAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Riprendi se sospeso (politica autoplay)
    if (globalAudioCtx.state === 'suspended') {
      globalAudioCtx.resume();
    }
    return globalAudioCtx;
  }, []);

  // ─── Utilità interne ────────────────────────────────────────────────────

  /**
   * Suona un oscillatore semplice con envelope ADSR lineare.
   * @param {object} opts
   * @param {number}   opts.freq        - frequenza Hz
   * @param {string}   opts.type        - 'sine'|'square'|'sawtooth'|'triangle'
   * @param {number}   opts.startTime   - AudioContext time di inizio
   * @param {number}   opts.duration    - durata totale in secondi
   * @param {number}   opts.gain        - volume picco (0–1)
   * @param {number}   [opts.attack]    - attack in secondi
   * @param {number}   [opts.decay]     - decay + sustain fino a 0, in secondi
   * @param {number}   [opts.freqEnd]   - frequenza finale (glide)
   */
  const playTone = useCallback((ctx, opts) => {
    const {
      freq, type = 'sine', startTime, duration,
      gain = 0.5, attack = 0.01, decay, freqEnd,
    } = opts;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    if (freqEnd !== undefined) {
      osc.frequency.linearRampToValueAtTime(freqEnd, startTime + duration);
    }

    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(gain, startTime + attack);

    const releaseStart = startTime + (decay ?? duration - 0.01);
    gainNode.gain.setValueAtTime(gain, releaseStart);
    gainNode.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.05);
  }, []);

  // ─── Suoni pubblici ─────────────────────────────────────────────────────

  /**
   * 1. Countdown beep — 3 note musicali distinte (Sol4, Si4, Re5).
   * n=3 → Sol (392 Hz), n=2 → Si (494 Hz), n=1 → Re5 (587 Hz)
   */
  const playCountdownBeep = useCallback((n) => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    const freqs = { 3: 392, 2: 494, 1: 587 };
    const freq = freqs[n] ?? 392;

    playTone(ctx, { freq, type: 'sine', startTime: t, duration: 0.22, gain: 0.55, attack: 0.005, decay: 0.1 });
    // Leggero armonico per spessore
    playTone(ctx, { freq: freq * 2, type: 'sine', startTime: t, duration: 0.18, gain: 0.15, attack: 0.005, decay: 0.08 });
  }, [getCtx, playTone]);

  /**
   * 2. STOP — buzzer deciso: sweep discendente + click
   */
  const playStop = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    // Sweep grave
    playTone(ctx, { freq: 440, type: 'square', startTime: t, duration: 0.18, gain: 0.4, attack: 0.005, freqEnd: 200, decay: 0.12 });
    // Click impattante
    playTone(ctx, { freq: 80, type: 'sine', startTime: t, duration: 0.08, gain: 0.6, attack: 0.001, decay: 0.05 });
  }, [getCtx, playTone]);

  /**
   * 3. Barra interruzione 5s scaduta — "dong" impattante
   */
  const playInterruptionEnd = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    // Nota fondamentale (bassa)
    playTone(ctx, { freq: 164.81, type: 'sine', startTime: t, duration: 1.8, gain: 0.6, attack: 0.005, decay: 1.2 }); // E3
    // Armonico superiore
    playTone(ctx, { freq: 329.63, type: 'sine', startTime: t, duration: 1.2, gain: 0.2, attack: 0.005, decay: 0.8 }); // E4
    // Strike impulsivo metallico
    playTone(ctx, { freq: 82.41, type: 'triangle', startTime: t, duration: 0.12, gain: 0.7, attack: 0.001, decay: 0.1 }); // E2
  }, [getCtx, playTone]);

  /**
   * 4. 60 secondi scaduti — gong: fondamentale lunga con decay lento
   */
  const playTimeUp = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;

    // Frequenza fondamentale del gong
    playTone(ctx, { freq: 110, type: 'sine', startTime: t, duration: 2.5, gain: 0.7, attack: 0.005, decay: 1.8 });
    // Parziale leggermente dissonante (carattere del gong)
    playTone(ctx, { freq: 275, type: 'sine', startTime: t, duration: 2.0, gain: 0.3, attack: 0.005, decay: 1.4 });
    playTone(ctx, { freq: 183, type: 'sine', startTime: t, duration: 1.8, gain: 0.2, attack: 0.005, decay: 1.2 });
    // Strike impulsivo iniziale
    playTone(ctx, { freq: 55, type: 'triangle', startTime: t, duration: 0.15, gain: 0.8, attack: 0.001, decay: 0.12 });
  }, [getCtx, playTone]);

  /**
   * 5. Nuova parola — tick/pop leggero e neutro
   */
  const playNewWord = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    playTone(ctx, { freq: 900, type: 'sine', startTime: t, duration: 0.06, gain: 0.25, attack: 0.002, decay: 0.04 });
  }, [getCtx, playTone]);

  /**
   * 6. Risposta corretta — ding doppio ascendente (Mi5, Sol5)
   */
  const playCorrect = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    playTone(ctx, { freq: 659, type: 'sine', startTime: t, duration: 0.15, gain: 0.5, attack: 0.005, decay: 0.1 });
    playTone(ctx, { freq: 784, type: 'sine', startTime: t + 0.14, duration: 0.2, gain: 0.5, attack: 0.005, decay: 0.14 });
  }, [getCtx, playTone]);

  /**
   * 7. Risposta sbagliata — buzz breve discendente
   */
  const playWrong = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    playTone(ctx, { freq: 220, type: 'sawtooth', startTime: t, duration: 0.18, gain: 0.35, attack: 0.005, freqEnd: 110, decay: 0.12 });
    playTone(ctx, { freq: 165, type: 'square', startTime: t + 0.05, duration: 0.13, gain: 0.2, attack: 0.005, freqEnd: 90, decay: 0.1 });
  }, [getCtx, playTone]);

  /**
   * 8. Fine partita — fanfara breve (Do5, Mi5, Sol5)
   */
  const playGameEnd = useCallback(() => {
    const ctx = getCtx();
    const t = ctx.currentTime;
    const notes = [523, 659, 784]; // C5, E5, G5
    notes.forEach((freq, i) => {
      const start = t + i * 0.16;
      playTone(ctx, { freq, type: 'sine', startTime: start, duration: 0.28, gain: 0.55, attack: 0.01, decay: 0.2 });
      // Armonico per ricchezza
      playTone(ctx, { freq: freq * 1.5, type: 'sine', startTime: start, duration: 0.22, gain: 0.15, attack: 0.01, decay: 0.16 });
    });
  }, [getCtx, playTone]);

  return useMemo(() => ({
    playCountdownBeep,
    playStop,
    playInterruptionEnd,
    playTimeUp,
    playNewWord,
    playCorrect,
    playWrong,
    playGameEnd,
  }), [
    playCountdownBeep,
    playStop,
    playInterruptionEnd,
    playTimeUp,
    playNewWord,
    playCorrect,
    playWrong,
    playGameEnd,
  ]);
};
